import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog, NotFoundState, showToast } from '@ideoz/ui';
import {
  useAppDispatch,
  useAppSelector,
  useStartups,
  useDiscussionRoom,
  useDiscussionConversations,
  useInvalidateDiscussionConversations,
  selectConversationId,
  resetConversation,
  setMode,
  setCurrentProjectId,
  setCurrentScopeId,
  setCurrentDiscussionRoomId,
  projectService,
} from '@ideoz/core';
import ConversationPage from './ConversationPage';

/**
 * Discussion Room workspace — `/:startupSlug/discussions/:conversationId`.
 *
 * The startup-level counterpart of ScopeConversationPage: same chat view, but
 * Atlas only (no scope, no mode pills, no Files/Artifacts). A reserved
 * `:conversationId` of `new` is an empty composer. The first message streams
 * to the AI room create endpoint; Nest only lists conversations.
 */
export default function DiscussionConversationPage(): JSX.Element {
  const { startupSlug, conversationId } = useParams<{
    startupSlug: string;
    conversationId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const {
    data: startups = [],
    isFetched: startupsFetched,
    isFetching: startupsFetching,
    isError: startupsError,
  } = useStartups();
  const matchedStartup = startups.find((s) => s.slug === startupSlug) ?? null;
  const startupId = matchedStartup?.id ?? null;

  const { data: room } = useDiscussionRoom(startupId);
  const roomId = room?.id ?? null;

  const { data: convData, isLoading: isLoadingConversations } = useDiscussionConversations(
    startupId,
    roomId
  );
  const conversations = convData ?? [];
  const invalidateConversations = useInvalidateDiscussionConversations();

  const isNewChat = conversationId === 'new';
  const activeConversationId = isNewChat ? null : (conversationId ?? null);
  const reduxConversationId = useAppSelector(selectConversationId);

  // Bind the streaming context to the room (scopeId stays null; the turn builder
  // skips scope auto-creation when a discussion room is set).
  useEffect(() => {
    if (startupId) dispatch(setCurrentProjectId(startupId));
    dispatch(setCurrentScopeId(null));
    if (roomId) dispatch(setCurrentDiscussionRoomId(roomId));
    dispatch(setMode('strategy'));
    return () => {
      dispatch(setCurrentProjectId(null));
      dispatch(setCurrentDiscussionRoomId(null));
    };
  }, [dispatch, startupId, roomId]);

  // The id we entered `…/new` with, so the redirect below ignores a stale id
  // from the chat we just left.
  const newChatBaselineRef = useRef<string | null>(null);

  useEffect(() => {
    if (isNewChat) {
      newChatBaselineRef.current = reduxConversationId ?? null;
      dispatch(resetConversation());
      dispatch(setMode('strategy'));
    } else {
      newChatBaselineRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capture on new-chat entry only
  }, [isNewChat, dispatch]);

  // Turn 1 (or a pre-message shell) assigns a conversation id. Move the URL
  // off `…/new` and refresh the sidebar list.
  useEffect(() => {
    if (!isNewChat || !reduxConversationId || !startupSlug) return;
    if (reduxConversationId === newChatBaselineRef.current) return;
    void invalidateConversations(startupId, roomId);
    navigate(`/${startupSlug}/discussions/${reduxConversationId}`, { replace: true });
  }, [
    isNewChat,
    reduxConversationId,
    startupSlug,
    startupId,
    roomId,
    navigate,
    invalidateConversations,
  ]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      if (startupSlug) navigate(`/${startupSlug}/discussions/${id}`);
    },
    [startupSlug, navigate]
  );

  const handleCreateConversation = useCallback(() => {
    if (startupSlug) navigate(`/${startupSlug}/discussions/new`);
  }, [startupSlug, navigate]);

  // ---- Delete conversation (from the sidebar recent-chats list) ----
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRequestDeleteConversation = useCallback(
    (id: string) => {
      const title = conversations.find((c) => c.id === id)?.title ?? '';
      setPendingDelete({ id, title });
    },
    [conversations]
  );

  const handleConfirmDeleteConversation = useCallback(async () => {
    if (!pendingDelete || !startupId) return;
    setIsDeleting(true);
    try {
      await projectService.deleteConversation(startupId, pendingDelete.id);
      await invalidateConversations(startupId, roomId);
      showToast({ message: t('conversationDelete.deleted'), severity: 'success' });
      // Deleting the open chat → re-resolve the room (opens the next one or
      // creates a fresh one).
      if (pendingDelete.id === activeConversationId && startupSlug) {
        navigate(`/${startupSlug}/discussions`, { replace: true });
      }
      setPendingDelete(null);
    } catch (err) {
      console.error('Failed to delete discussion:', err);
      showToast({ message: t('conversationDelete.deleteFailed'), severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [
    pendingDelete,
    startupId,
    roomId,
    startupSlug,
    activeConversationId,
    invalidateConversations,
    navigate,
    t,
  ]);

  // 404 for a bad slug — only after the startups query settles.
  if (startupsFetched && !startupsFetching && !startupsError && (!matchedStartup || !startupId)) {
    return (
      <NotFoundState
        description="Startup not found"
        actionLabel="Back to My Startups"
        onAction={() => navigate('/startups')}
      />
    );
  }

  return (
    <>
      <ConversationPage
        sidebarPageType="scope"
        urlConversationIdOverride={activeConversationId}
        routeProjectId={startupId}
        scopeModeControl={{
          // Atlas-only. The AI pins strategy on this path, so the pills are
          // hidden and mode changes are a no-op.
          activeMode: 'strategy',
          onModeChange: () => {},
          conversations: conversations.map((c) => ({ id: c.id, title: c.title })),
          isLoadingConversations,
          activeConversationId,
          onSelectConversation: handleSelectConversation,
          onCreateConversation: handleCreateConversation,
          onDeleteConversation: handleRequestDeleteConversation,
          onBack: () => navigate(startupSlug ? `/${startupSlug}/work-scopes` : '/startups'),
          onConversationNotFound: () => {
            if (startupSlug) navigate(`/${startupSlug}/discussions`, { replace: true });
          },
          scopeTitle: room?.title ?? t('nav.discussions'),
          // No Files / Artifacts in the Discussion Room (phase one): omit the
          // sidebar items entirely rather than showing them disabled.
          hideFilesAndArtifacts: true,
        }}
      />
      <ConfirmDialog
        open={!!pendingDelete}
        variant="danger"
        title={t('conversationDelete.title')}
        description={
          pendingDelete?.title
            ? t('conversationDelete.desc', { title: pendingDelete.title })
            : t('conversationDelete.descUntitled')
        }
        confirmLabel={t('conversationDelete.confirm')}
        cancelLabel={t('conversationDelete.cancel')}
        isLoading={isDeleting}
        onConfirm={handleConfirmDeleteConversation}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
